import axios from "axios";

const API = axios.create({
  baseURL: "/api",
});

// ======================================================
// GET TOKEN
// ======================================================

const getToken = () => {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("userToken") ||
    localStorage.getItem("authToken")
  );
};

// ======================================================
// GET EVENTS FOR WEBSITE
// GET /api/events
// ======================================================

export const getEvents = async () => {
  try {
    const response = await API.get("/events");

    return response.data;
  } catch (error) {
    console.error(
      "❌ Get Events Error:",
      error
    );

    throw (
      error.response?.data || {
        message: "Unable to fetch events.",
      }
    );
  }
};

// ======================================================
// GET SINGLE EVENT
// GET /api/events/:id
// ======================================================

export const getEventById = async (id) => {
  try {
    const response = await API.get(
      `/events/${id}`
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Get Event Error:",
      error
    );

    throw (
      error.response?.data || {
        message: "Unable to fetch event.",
      }
    );
  }
};

// ======================================================
// ADMIN - GET ALL EVENTS
// GET /api/events/admin/all
// ======================================================

export const getAllEventsAdmin = async () => {
  try {
    const token = getToken();

    const response = await API.get(
      "/events/admin/all",
      {
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Get Admin Events Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Unable to fetch admin events.",
      }
    );
  }
};

// ======================================================
// ADMIN - CREATE EVENT
// POST /api/events
// ======================================================

export const createEvent = async (
  eventData
) => {
  try {
    const token = getToken();

    const response = await API.post(
      "/events",
      eventData,
      {
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Create Event Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Event creation failed.",
      }
    );
  }
};

// ======================================================
// ADMIN - UPDATE EVENT
// PUT /api/events/:id
// ======================================================

export const updateEvent = async (
  id,
  eventData
) => {
  try {
    const token = getToken();

    const response = await API.put(
      `/events/${id}`,
      eventData,
      {
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Update Event Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Event update failed.",
      }
    );
  }
};

// ======================================================
// ADMIN - DELETE EVENT
// DELETE /api/events/:id
// ======================================================

export const deleteEvent = async (id) => {
  try {
    const token = getToken();

    const response = await API.delete(
      `/events/${id}`,
      {
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Delete Event Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Event deletion failed.",
      }
    );
  }
};

// ======================================================
// ADMIN - ACTIVATE / DEACTIVATE EVENT
// PUT /api/events/status/:id
// ======================================================

export const toggleEventStatus = async (
  id
) => {
  try {
    const token = getToken();

    const response = await API.put(
      `/events/status/${id}`,
      {},
      {
        headers: {
          ...(token && {
            Authorization: `Bearer ${token}`,
          }),
        },
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Toggle Event Status Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Unable to change event status.",
      }
    );
  }
};

// ======================================================
// USER - SUBMIT EVENT ACTIVITY
// POST /api/event-submissions
//
// FormData:
// event
// activity
// activityTitle
// description
// proof
// ======================================================

export const submitEventParticipation = async (
  formData
) => {
  try {
    const token = getToken();

    if (!token) {
      throw new Error(
        "Please login first."
      );
    }

    const response = await API.post(
      "/event-submissions",
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          // Do NOT manually set Content-Type.
          // Axios will automatically set multipart/form-data
          // boundary for FormData.
        },
      }
    );

    console.log(
      "✅ Activity submitted:",
      response.data
    );

    return response.data;
  } catch (error) {
    console.error(
      "❌ Event Submission Error:",
      error
    );

    throw (
      error.response?.data || {
        message:
          "Unable to submit event activity.",
      }
    );
  }
};

// ======================================================
// USER - GET MY SUBMISSIONS
// GET /api/event-submissions/my
// ======================================================

export const getMyEventSubmissions =
  async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.get(
        "/event-submissions/my",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ My Submissions Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to fetch your submissions.",
        }
      );
    }
  };

// ======================================================
// PUBLIC WEBSITE
// GET APPROVED / PUBLISHED ACTIVITIES
//
// IMPORTANT:
// NO TOKEN REQUIRED
//
// GET /api/event-submissions/approved
// ======================================================

export const getApprovedEventSubmissions =
  async () => {
    try {
      console.log(
        "🌍 Loading published event activities..."
      );

      const response = await API.get(
        "/event-submissions/approved"
      );

      console.log(
        "✅ Published activities response:",
        response.data
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Approved Submissions Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to fetch published activities.",
        }
      );
    }
  };

// ======================================================
// ALIAS
//
// Agar Event.jsx mein kisi old name ka import hai
// to wo bhi work karega.
// ======================================================

export const getPublishedEventActivities =
  getApprovedEventSubmissions;

// ======================================================
// ADMIN - GET PENDING SUBMISSIONS
// GET /api/event-submissions/pending
// ======================================================

export const getPendingEventSubmissions =
  async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.get(
        "/event-submissions/pending",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Pending Submissions Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to fetch pending submissions.",
        }
      );
    }
  };

// ======================================================
// ADMIN - GET APPROVED SUBMISSIONS
// GET /api/event-submissions/admin/approved
// ======================================================

export const getAllEventSubmissions =
  async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.get(
        "/event-submissions/admin/approved",
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Admin Approved Submissions Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to fetch approved submissions.",
        }
      );
    }
  };

// ======================================================
// ADMIN - APPROVE SUBMISSION
// PUT /api/event-submissions/:id/approve
// ======================================================

export const approveEventSubmission =
  async (id) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.put(
        `/event-submissions/${id}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Approve Submission Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to approve submission.",
        }
      );
    }
  };

// ======================================================
// ADMIN - REJECT SUBMISSION
// PUT /api/event-submissions/:id/reject
// ======================================================

export const rejectEventSubmission =
  async (id) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.put(
        `/event-submissions/${id}/reject`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Reject Submission Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to reject submission.",
        }
      );
    }
  };

// ======================================================
// ADMIN - DELETE SUBMISSION
// DELETE /api/event-submissions/:id
// ======================================================

export const deleteEventSubmission =
  async (id) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error(
          "Please login first."
        );
      }

      const response = await API.delete(
        `/event-submissions/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ Delete Submission Error:",
        error
      );

      throw (
        error.response?.data || {
          message:
            "Unable to delete submission.",
        }
      );
    }
  };